import { useRef, useState } from "react";
import type { ComposerCart } from "./orderBuild";
import { useT } from "../../i18n/LanguageProvider";
import { PHOTO_ACCEPT, isAcceptedPhoto } from "../usePhotoCapture";

/** One thumbnail in the describe-mode photo grid (draft or already-saved). */
export interface DescribePhotoThumb {
  key: string;
  previewUrl: string;
}

interface Props {
  cart: ComposerCart;
  onCartChange(next: ComposerCart): void;
  customerNote: string;
  onCustomerNoteChange(next: string): void;
  photos: DescribePhotoThumb[];
  photoCap: number;
  onAddFiles(files: File[]): void;
  onRemovePhoto(key: string): void;
  error: string | null;
  onContinue(): void;
}


/**
 * G-15 describe mode — the phone-first way to send an order: "10 roses in
 * this manner, customer also said X" + reference photos, instead of composing
 * stem by stem. The description text is the required core (it is what the
 * server accepts a lineless order on); photos and the customer's note are
 * optional. The florist realizes the bouquet on the floor — stock is only
 * consumed there.
 */
export function DescribeBouquet({
  cart,
  onCartChange,
  customerNote,
  onCustomerNoteChange,
  photos,
  photoCap,
  onAddFiles,
  onRemovePhoto,
  error,
  onContinue,
}: Props) {
  const { t } = useT();
  const libraryInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const remaining = photoCap - photos.length;
  // D-66 made the camera input `image/*`; on a desktop that offers every
  // image type, so what the server would refuse is refused here, by name.
  const [typeError, setTypeError] = useState<string | null>(null);

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = [...list];
    const accepted = files.filter(isAcceptedPhoto);
    setTypeError(accepted.length < files.length ? t("photoInput.unsupportedType") : null);
    if (accepted.length === 0) return;
    onAddFiles(accepted.slice(0, Math.max(0, remaining)));
  };

  return (
    <div className="pob-describe">
      {error ? <div className="alert error">{error}</div> : null}
      {typeError ? <div className="alert error">{typeError}</div> : null}

      <div className="card pob-reference-card">
        <strong>
          {t("describe.referencePhotos", { count: photos.length, cap: photoCap })}
        </strong>
        {photos.length > 0 ? (
          <div className="pob-photo-grid">
            {photos.map((photo) => (
              <div key={photo.key} className="pob-photo-thumb">
                <img src={photo.previewUrl} alt={t("describe.photoAlt")} />
                <button
                  type="button"
                  className="pob-photo-remove"
                  aria-label={t("photoInput.removePhoto")}
                  onClick={() => onRemovePhoto(photo.key)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">
            {t("describe.photoHint")}
          </p>
        )}
        <div className="button-row">
          <button
            type="button"
            className="button tinted"
            onClick={() => cameraInputRef.current?.click()}
            disabled={remaining <= 0}
          >
            {t("common.takePhoto")}
          </button>
          <button
            type="button"
            className="button tinted"
            onClick={() => libraryInputRef.current?.click()}
            disabled={remaining <= 0}
          >
            {t("describe.addFromLibrary")}
          </button>
        </div>
        <input
          ref={libraryInputRef}
          type="file"
          accept={PHOTO_ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          // D-66: exactly "image/*" — the tablet shell (Capacitor) opens the
          // camera only for that literal; PHOTO_ACCEPT sends it to the gallery.
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      <div className="card pob-describe-card">
        <div className="field">
          <label htmlFor="pob-describe-notes">
            {t("describe.describeLabel")}
          </label>
          <textarea
            id="pob-describe-notes"
            rows={4}
            value={cart.bouquetNotes}
            onChange={(e) => onCartChange({ ...cart, bouquetNotes: e.target.value })}
            placeholder={t("describe.describePlaceholder")}
          />
          <small className="hint">
            {t("describe.describeHint")}
          </small>
        </div>

        <div className="field">
          <label htmlFor="pob-describe-name">{t("describe.nameLabel")}</label>
          <input
            id="pob-describe-name"
            value={cart.bouquetName}
            onChange={(e) => onCartChange({ ...cart, bouquetName: e.target.value })}
            placeholder={t("describe.namePlaceholder")}
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label htmlFor="pob-describe-price">{t("describe.priceLabel")}</label>
          <input
            id="pob-describe-price"
            type="number"
            min={0}
            step="0.01"
            value={cart.bouquetPriceEur}
            onChange={(e) =>
              onCartChange({ ...cart, bouquetPriceEur: e.target.value })
            }
            placeholder="0.00"
          />
          <small className="hint">
            {t("describe.priceHint")}
          </small>
        </div>

        <div className="field">
          <label htmlFor="pob-describe-customer-note">
            {t("describe.customerNoteLabel")}
          </label>
          <textarea
            id="pob-describe-customer-note"
            rows={2}
            value={customerNote}
            onChange={(e) => onCustomerNoteChange(e.target.value)}
            placeholder={t("describe.customerNotePlaceholder")}
          />
          <small className="hint">
            {t("describe.customerNoteHint")}
          </small>
        </div>
      </div>

      <div className="button-row pob-describe-actions">
        <button type="button" className="button" onClick={onContinue}>
          {t("describe.continueToItems")}
        </button>
      </div>
    </div>
  );
}
